import { useEffect, useState } from 'react';
import { ArrowRight, RefreshCw, TriangleAlert } from 'lucide-react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { workspaces } from '@/api/engine';
import type { WorkspaceMembershipResponse } from '@/api/types';
import { resolvePostLoginLanding, type CustomerVerticalLanding } from '@/api/billing';
import { getUser, refreshAuthSession } from '@/lib/auth';
import { surfacesApi, type SurfaceSnapshot } from '@/api/surfaces';
import { homePathFor } from '@/lib/surfaceManifest';
import { useRouteNotice } from '@/components/routeNoticeContext';

type JoinState =
  | { status: 'joining' }
  | { status: 'joined'; workspaceName: string | null; verticals: CustomerVerticalLanding[] }
  | { status: 'error'; message: string };

function inviteErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Could not accept this workspace invite.';
}

const inviteAcceptanceRequests = new Map<string, Promise<WorkspaceMembershipResponse>>();

function acceptInviteOnce(token: string): Promise<WorkspaceMembershipResponse> {
  const existing = inviteAcceptanceRequests.get(token);
  if (existing) return existing;

  const request = workspaces.acceptInvite(token)
    .finally(() => {
      inviteAcceptanceRequests.delete(token);
    });
  inviteAcceptanceRequests.set(token, request);
  return request;
}

async function resolveBoardOrRoleHome(): Promise<{
  destination: string;
  snapshot: SurfaceSnapshot | null;
}> {
  try {
    const snapshot = await surfacesApi.getMySurfaces();
    const visibleSurfaces = new Set(snapshot.visible_surfaces);
    return {
      destination: visibleSurfaces.has('board') ? '/board' : homePathFor(visibleSurfaces),
      snapshot,
    };
  } catch {
    // Fail closed to the universal customer-safe home. This is also where the
    // former /board guard chain ultimately landed when surface resolution failed.
    return { destination: '/research', snapshot: null };
  }
}

export function WorkspaceJoinPage() {
  const { token: rawToken } = useParams<{ token: string }>();
  const token = rawToken?.trim() ?? '';
  const navigate = useNavigate();
  const location = useLocation();
  const { announce } = useRouteNotice();
  const { mutate } = useSWRConfig();
  const [searchParams] = useSearchParams();
  // Origin hint: the surface that minted the invite appends ?to=<vertical_id>
  // (e.g. the HenniPenny lab roster appends to=hennipenny) so a brand-new
  // invitee is never quizzed to pick between products they cannot tell apart.
  // A HINT only — it selects among the verticals the caller is actually
  // admitted to (server-resolved below); an unknown value is ignored.
  const verticalHint = searchParams.get('to')?.trim().toLowerCase() ?? '';
  const [state, setState] = useState<JoinState>({ status: 'joining' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: 'This invite link is missing its token.' });
      return;
    }

    let cancelled = false;
    setState({ status: 'joining' });

    acceptInviteOnce(token)
      .then(async (membership) => {
        await refreshAuthSession({ allowMissingEndpoint: true });
        // A lab MEMBER's home is their vertical app, not the platform board
        // (which their role cannot even see, and which sits behind the seity
        // gate). Reuse the bare-login landing resolution: unambiguous single
        // vertical -> straight redirect; several -> explicit Continue buttons;
        // operators / no verticals / resolver failure -> authoritative role home.
        let landingVerticals: CustomerVerticalLanding[] = [];
        try {
          const landing = await resolvePostLoginLanding();
          if (landing.redirect_url) {
            window.location.assign(landing.redirect_url);
            return;
          }
          if (!landing.is_operator) {
            landingVerticals = landing.verticals;
            // The invite's origin hint resolves the multi-vertical ambiguity:
            // if the door the invite was minted from is among the caller's
            // admitted verticals, carry them straight through — no choice
            // screen on first arrival.
            const hinted = verticalHint
              ? landingVerticals.find((v) => v.vertical_id.toLowerCase() === verticalHint)
              : undefined;
            if (hinted) {
              window.location.assign(hinted.customer_home_url);
              return;
            }
          }
        } catch {
          // Landing resolution is best-effort; fall through to the surface-resolved home.
        }
        if (cancelled) return;
        if (landingVerticals.length > 0) {
          setState({
            status: 'joined',
            workspaceName: membership?.thread?.name ?? null,
            verticals: landingVerticals,
          });
        } else {
          const { destination, snapshot } = await resolveBoardOrRoleHome();
          if (cancelled) return;
          const user = getUser();
          if (snapshot && user?.uid) {
            // The shell queried /me/surfaces before this tenant-less invitee joined
            // and may hold a fail-closed error in SWR. Seed the refreshed snapshot
            // so the destination guard cannot bounce a newly admitted operator.
            await mutate(['me:surfaces', user.uid], snapshot, { revalidate: false });
          }
          announce({
            message: 'Workspace invite accepted.',
            tone: 'success',
            destination,
            originKey: location.key,
          });
          navigate(destination, { replace: true });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({ status: 'error', message: inviteErrorMessage(error) });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [announce, attempt, location.key, mutate, navigate, token, verticalHint]);

  return (
    <section
      aria-labelledby="workspace-join-title"
      style={{
        minHeight: 'calc(100vh - 76px)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <div
        style={{
          width: 'min(100%, 440px)',
          background: 'var(--sp-bg-elev)',
          border: '1px solid var(--sp-border)',
          borderRadius: 8,
          padding: 24,
        }}
      >
        {state.status === 'joining' ? (
          <div role="status" aria-live="polite">
            <div className="sp-brand mono" id="workspace-join-title" style={{ marginBottom: 8 }}>
              Joining workspace
            </div>
            <p style={{ margin: 0, color: 'var(--sp-text-muted)' }}>
              Accepting your invite...
            </p>
          </div>
        ) : state.status === 'joined' ? (
          <div role="status" aria-live="polite">
            <div className="sp-brand mono" id="workspace-join-title" style={{ marginBottom: 8 }}>
              {state.workspaceName ? `You're in — ${state.workspaceName}` : "You're in"}
            </div>
            <p style={{ margin: '0 0 16px', color: 'var(--sp-text-muted)' }}>
              Your invite is accepted. Continue to your workspace:
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {state.verticals.map((vertical) => (
                <button
                  key={vertical.vertical_id}
                  type="button"
                  className="sp-btn sp-btn-primary"
                  onClick={() => window.location.assign(vertical.customer_home_url)}
                >
                  Continue to {vertical.display_name}
                  <ArrowRight size={14} aria-hidden="true" style={{ marginLeft: 6, verticalAlign: -2 }} />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
                color: 'var(--sp-error)',
                fontWeight: 700,
              }}
            >
              <TriangleAlert size={18} aria-hidden="true" />
              <span id="workspace-join-title">Invite could not be accepted</span>
            </div>
            <p style={{ margin: '0 0 16px', color: 'var(--sp-text-muted)' }}>{state.message}</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {token && (
                <button
                  type="button"
                  className="sp-btn"
                  onClick={() => setAttempt((value) => value + 1)}
                >
                  <RefreshCw size={14} aria-hidden="true" style={{ marginRight: 6, verticalAlign: -2 }} />
                  Try again
                </button>
              )}
              <button
                type="button"
                className="sp-btn sp-btn-primary"
                onClick={() => navigate('/board')}
              >
                Open board
                <ArrowRight size={14} aria-hidden="true" style={{ marginLeft: 6, verticalAlign: -2 }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
